function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import applyReducer, { reduceReducers } from './applyReducer';
import cabinetFilterOptionsConstants from '../constants/cabinetFilterOptionsConstants';
import entityReducerFactory from './factories/entityReducerFactory';
const entityReducer = entityReducerFactory('cabinetFilterOptions');
const initState = {
  initialIndexQuery: _objectSpread(_objectSpread({}, entityReducer.initState.initialIndexQuery), {}, {
    sortDirection: null // avoid specifying server-side column sorting by default
  })
};

/**
 *  The server refuses to build options for an index the cabinet does not declare in its
 *  filterDefinitions. That answer never changes, which is what lets the rail drop the filter
 *  instead of retrying it. The response carries no machine readable code today, so the message
 *  is the only signal available; replace this with a code once the server sends one.
 */
const UNDEFINED_FILTER_PATTERN = /unable to find filter definition/i;
const serverErrorText = error => {
  const {
    data = {}
  } = (error === null || error === void 0 ? void 0 : error.response) || {};
  return [data.error_msg, data.error_debug, error === null || error === void 0 ? void 0 : error.message].filter(text => typeof text === 'string').join('\n');
};
const isFilterUndefinedOnServer = error => UNDEFINED_FILTER_PATTERN.test(serverErrorText(error));

/**
 *  Avoid repeated serverIndex calls by setting state[currentCabinet][currentFilter].loading
 */
const index = state => {
  const {
    currentCabinet,
    currentFilter
  } = state;
  let cabinetFilterOptionState = {
    loading: true
  };
  if (state[currentCabinet] && state[currentCabinet][currentFilter]) {
    cabinetFilterOptionState = _objectSpread(_objectSpread({}, state[currentCabinet][currentFilter]), {}, {
      loading: true
    });
  }
  return _objectSpread(_objectSpread({}, state), {}, {
    [currentCabinet]: _objectSpread(_objectSpread({}, state[currentCabinet]), {}, {
      [currentFilter]: cabinetFilterOptionState
    })
  });
};

/**
 *  On failure move the error to the filter's state, flagging the filters the cabinet does not
 *  define so they can be left out of the rail rather than requested again.
 */
const indexFail = (state, {
  data
} = {}) => {
  var _state$currentCabinet;
  const {
    currentCabinet,
    currentFilter,
    error,
    loading
  } = state;
  const cabinetFilterOptionState = _objectSpread(_objectSpread({}, (_state$currentCabinet = state[currentCabinet]) === null || _state$currentCabinet === void 0 ? void 0 : _state$currentCabinet[currentFilter]), {}, {
    loading,
    error
  });
  if (isFilterUndefinedOnServer(data)) {
    cabinetFilterOptionState.undefinedOnServer = true;
  }

  // set the cabinet filter-specific state to the top-level and clean up
  const updatedState = _objectSpread(_objectSpread({}, state), {}, {
    // update the current cabinet with the top-level state
    [currentCabinet]: {
      [currentFilter]: cabinetFilterOptionState
    }
  });
  delete updatedState.error;
  delete updatedState.loading;
  return updatedState;
};

/**
 *  Store index data in cabinet-specific data objects to allow multiple simultaneous cabinets
 *  without reloading. entityActionFactory puts all requests in the top-level state, move this
 *  to state[current cabinet][current filter]
 */
const indexSuccess = state => {
  const {
    currentCabinet,
    currentFilter,
    initialIndexQuery
  } = state;

  // remove everything not part of the entity state for queries
  const entityResponseProps = Object.keys(entityReducer.initState);
  const cabinetFilterOptionState = {};
  entityResponseProps.forEach(prop => {
    var _state$indexQuery;
    // handle infinite scroll paging by adding to the list
    if (prop === 'list' && (state === null || state === void 0 || (_state$indexQuery = state.indexQuery) === null || _state$indexQuery === void 0 || (_state$indexQuery = _state$indexQuery.paging) === null || _state$indexQuery === void 0 ? void 0 : _state$indexQuery.current) > 0) {
      var _state$currentCabinet2;
      const newData = state.list;
      const existingData = (state === null || state === void 0 || (_state$currentCabinet2 = state[currentCabinet]) === null || _state$currentCabinet2 === void 0 ? void 0 : _state$currentCabinet2[currentFilter].list) || [];
      const existingIDs = existingData.map(e => e.id);

      // the 1st new result will be somewhere in the existing list
      cabinetFilterOptionState.list = [...existingData]; // avoid mutating original object
      newData.forEach(nd => {
        if (!existingIDs.includes(nd.id)) {
          cabinetFilterOptionState.list.push(nd);
        }
      });
    } else {
      cabinetFilterOptionState[prop] = state[prop];
    }
  });

  // Ensure loading is explicitly set to false.
  // This prevents the loading state from persisting after a successful request,
  // which would block subsequent requests from executing (they would be queued indefinitely).
  // This matches the pattern used in summaryFilterOptionsReducer and deferredReducer.
  cabinetFilterOptionState.loading = false;

  // set the cabinet filter-specific state to the top-level and clean up
  // NOTE: We must explicitly set loading: false at the top level because we're
  // returning a new object (not spreading ...state). The entityReducer sets
  // loading: false, but our custom reducer replaces the entire state object.
  // Without top-level loading: false, the pendingQueryQueue logic in
  // entityActionFactory may not process queued requests correctly.
  const updatedState = {
    currentCabinet,
    currentFilter,
    initialIndexQuery,
    loading: false,
    // Preserve top-level loading state for request queue logic
    // update the current cabinet with the top-level state
    [currentCabinet]: _objectSpread(_objectSpread({}, state[currentCabinet]), {}, {
      [currentFilter]: cabinetFilterOptionState
    })
  };
  return updatedState;
};
const setCurrent = (state, {
  currentCabinet,
  currentFilter
}) => _objectSpread(_objectSpread({}, state), {}, {
  currentCabinet,
  currentFilter
});
const reducers = {
  [cabinetFilterOptionsConstants.INDEX]: reduceReducers(entityReducer.reducers[cabinetFilterOptionsConstants.INDEX], index),
  [cabinetFilterOptionsConstants.INDEX_FAIL]: reduceReducers(entityReducer.reducers[cabinetFilterOptionsConstants.INDEX_FAIL], indexFail),
  [cabinetFilterOptionsConstants.INDEX_SUCCESS]: reduceReducers(entityReducer.reducers[cabinetFilterOptionsConstants.INDEX_SUCCESS], indexSuccess),
  [cabinetFilterOptionsConstants.SET_CURRENT]: setCurrent
};
export default applyReducer(reducers, initState);